import React from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';

import { color, r, radius } from '../../theme/tokens';

/**
 * EntryStateNotice — the one-line notice under an analysed row's chips when
 * its amount cannot be compared to the studies ("1c", Tanu, 2026-09-24): a
 * short line saying why, ending in a single button that opens the existing
 * dose editor.
 *
 * Also carries the safety-limit line ("The studied dose (12 mg) is above the
 * safe daily limit…"), with no action, since there is nothing to fix.
 *
 * The chips stay as they are; this sits beneath them. The button keeps the
 * chip family's shape and size — radius, padding, 12px bold — so it reads as
 * part of the row rather than as a second, larger control set.
 *
 * **No `onAction`, no button.** Read-only surfaces (the view page and modal)
 * pass none: a disabled button styled like a live one sat beside "Check the
 * weight" and did nothing when pressed (review, 2026-09-27). The line alone
 * still says why the amount is not compared.
 */
const EntryStateNotice = ({ message, actionLabel, onAction, supplementName }) => (
  <Box
    data-testid="entry-state-notice"
    sx={{
      marginTop: r(12),
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: r(10),
      padding: `${r(10)} ${r(12)}`,
      backgroundColor: color.surface,
      borderRadius: radius.md,
      fontSize: r(13),
      lineHeight: 1.45,
      color: color.ink,
    }}
  >
    <Box component="span" sx={{ flex: '1 1 12rem', minWidth: 0 }}>{message}</Box>
    {onAction && (
      <ButtonBase
        onClick={onAction}
        aria-label={`${actionLabel} for ${supplementName}`}
        sx={{
          padding: `${r(6)} ${r(11)}`,
          border: `${r(1)} solid ${color.line}`,
          borderRadius: radius.sm,
          backgroundColor: color.paper,
          fontSize: r(12),
          fontWeight: 700,
          color: color.evergreen,
          whiteSpace: 'nowrap',
          '&:hover': {
            borderColor: color.accent,
            color: color.accent,
          },
          '&:focus-visible': {
            outline: `${r(2)} solid ${color.accent}`,
            outlineOffset: r(2),
          },
        }}
      >
        {actionLabel}
      </ButtonBase>
    )}
  </Box>
);

export default EntryStateNotice;
